import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Download,
  ExternalLink,
  Loader2,
  Save,
  Smartphone,
} from 'lucide-react';
import {
  AppConfigDto,
  AppKey,
  APP_VERSION_LABELS,
  DEFAULT_APPSTORE_URLS,
  DEFAULT_PLAYSTORE_URLS,
  emptyPlatformConfig,
  getAppConfig,
  getLiveStoreVersions,
  LiveStorePlatformVersion,
  PlatformVersionConfig,
  sendUpdateNotification,
  updateAppConfig,
  UpdateType,
} from '../../services/MasterAdmin/appConfigService';

export interface AppVersionControlSectionProps {
  appKey: AppKey;
  embedded?: boolean;
}

type PlatformKey = 'android' | 'ios';

function normalizeSlice(
  slice: PlatformVersionConfig | undefined,
  appKey: AppKey,
  platform: PlatformKey
): PlatformVersionConfig {
  const fallback = emptyPlatformConfig(appKey, platform);
  return {
    latestVersion: slice?.latestVersion || fallback.latestVersion,
    minRequiredVersion: slice?.minRequiredVersion || fallback.minRequiredVersion,
    latestVersionCode: Number.isFinite(slice?.latestVersionCode)
      ? Math.max(0, Number(slice?.latestVersionCode))
      : fallback.latestVersionCode,
    minRequiredVersionCode: Number.isFinite(slice?.minRequiredVersionCode)
      ? Math.max(0, Number(slice?.minRequiredVersionCode))
      : fallback.minRequiredVersionCode,
    updateType: (slice?.updateType as UpdateType) || 'optional',
    storeUrl: slice?.storeUrl?.trim() || fallback.storeUrl,
    disableUpdateCheck: Boolean(slice?.disableUpdateCheck),
  };
}

function applyLiveSlice(
  current: PlatformVersionConfig,
  live: LiveStorePlatformVersion | undefined
): PlatformVersionConfig {
  if (!live) return current;
  const next = { ...current };
  if (live.latestVersion) next.latestVersion = live.latestVersion;
  if (live.latestVersionCode != null && live.latestVersionCode > 0) {
    next.latestVersionCode = live.latestVersionCode;
  }
  if (live.storeUrl?.trim()) next.storeUrl = live.storeUrl.trim();
  return next;
}

function forceLocksCurrentBuild(slice: PlatformVersionConfig): boolean {
  return (
    slice.updateType === 'force' &&
    slice.latestVersion.trim() === slice.minRequiredVersion.trim() &&
    Number(slice.latestVersionCode) === Number(slice.minRequiredVersionCode)
  );
}

function PlatformCard({
  platform,
  value,
  onChange,
  appKey,
}: {
  platform: PlatformKey;
  value: PlatformVersionConfig;
  onChange: (next: PlatformVersionConfig) => void;
  appKey: AppKey;
}): React.ReactElement {
  const isIos = platform === 'ios';
  const accent = isIos
    ? {
        ring: 'border-slate-200 dark:border-slate-700',
        header: 'from-slate-900 to-slate-700',
        badge: 'bg-white/15 text-white',
        inputFocus: 'focus:ring-slate-500',
      }
    : {
        ring: 'border-emerald-200 dark:border-emerald-800/60',
        header: 'from-emerald-700 to-emerald-600',
        badge: 'bg-white/15 text-white',
        inputFocus: 'focus:ring-emerald-500',
      };

  const inputClass = `w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 ${accent.inputFocus}`;
  const locksCurrent = forceLocksCurrentBuild(value);

  return (
    <section className={`rounded-xl border ${accent.ring} overflow-hidden bg-white dark:bg-gray-800 shadow-sm`}>
      <div className={`px-4 py-3 bg-gradient-to-r ${accent.header} text-white flex items-start justify-between gap-3`}>
        <div>
          <div className="flex items-center gap-2">
            <Smartphone className="w-4 h-4" />
            <h3 className="text-sm font-semibold tracking-wide">
              {isIos ? 'iOS · App Store' : 'Android · Play Store'}
            </h3>
          </div>
          <p className="text-xs text-white/80 mt-1">
            {isIos
              ? 'Marketing version from App Store. Build from devices that opened the app.'
              : 'Marketing version from Play Store. Build from devices that opened the app.'}
          </p>
        </div>
        <span className={`shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full ${accent.badge}`}>
          {value.disableUpdateCheck ? 'Override on' : value.updateType === 'force' ? 'Force' : 'Optional'}
        </span>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Latest version
            </span>
            <input
              type="text"
              value={value.latestVersion}
              onChange={(e) => onChange({ ...value, latestVersion: e.target.value })}
              placeholder="e.g. 1.0.100"
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min required version
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    minRequiredVersion: value.latestVersion,
                    minRequiredVersionCode: value.latestVersionCode,
                  })
                }
                className="text-[11px] font-normal text-primary-600 hover:underline"
              >
                Match latest
              </button>
            </span>
            <input
              type="text"
              value={value.minRequiredVersion}
              onChange={(e) => onChange({ ...value, minRequiredVersion: e.target.value })}
              placeholder="e.g. 1.0.90"
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Latest build number
            </span>
            <input
              type="number"
              min={0}
              value={value.latestVersionCode}
              onChange={(e) =>
                onChange({ ...value, latestVersionCode: Math.max(0, parseInt(e.target.value, 10) || 0) })
              }
              placeholder={isIos ? 'CFBundleVersion' : 'Play versionCode'}
              className={inputClass}
            />
            <span className="mt-1 block text-[11px] text-gray-500 dark:text-gray-400">
              {isIos ? 'App Store Connect build, not Play’s 10000+ CI code.' : 'Play Console versionCode (CI 10000+run).'}
            </span>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min required build number
            </span>
            <input
              type="number"
              min={0}
              value={value.minRequiredVersionCode}
              onChange={(e) =>
                onChange({
                  ...value,
                  minRequiredVersionCode: Math.max(0, parseInt(e.target.value, 10) || 0),
                })
              }
              placeholder={isIos ? 'e.g. live iOS build' : 'e.g. 10020'}
              className={inputClass}
            />
          </label>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Update type
          </span>
          <select
            value={value.updateType}
            onChange={(e) => onChange({ ...value, updateType: e.target.value as UpdateType })}
            className={inputClass}
          >
            <option value="optional">Optional (user can skip if above min)</option>
            <option value="force">Force (must update to continue)</option>
          </select>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {isIos ? 'App Store URL' : 'Play Store URL'}
          </span>
          <div className="flex gap-2">
            <input
              type="url"
              value={value.storeUrl}
              onChange={(e) => onChange({ ...value, storeUrl: e.target.value })}
              placeholder={isIos ? DEFAULT_APPSTORE_URLS[appKey] : DEFAULT_PLAYSTORE_URLS[appKey]}
              className={inputClass}
            />
            {value.storeUrl ? (
              <a
                href={value.storeUrl}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 inline-flex items-center justify-center w-10 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                title="Open store listing"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : null}
          </div>
        </label>

        {locksCurrent && !value.disableUpdateCheck && (
          <div className="flex gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-200">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              Min required equals latest on this platform. Anyone below this exact store build will be
              locked on the update screen. Only do this after that build is live in the store.
            </p>
          </div>
        )}

        <label
          htmlFor={`disableUpdateCheck-${platform}-${appKey}`}
          className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 cursor-pointer"
        >
          <input
            type="checkbox"
            id={`disableUpdateCheck-${platform}-${appKey}`}
            checked={value.disableUpdateCheck}
            onChange={(e) => onChange({ ...value, disableUpdateCheck: e.target.checked })}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          <span>
            <span className="block text-sm font-semibold text-amber-900 dark:text-amber-200">
              Emergency override: disable app update requirement
            </span>
            <span className="block text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              {isIos
                ? 'Skip the App Store update screen for iOS users of this app.'
                : 'Skip the Play Store update screen for Android users of this app.'}
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

function AppVersionControlSection({
  appKey,
  embedded = false,
}: AppVersionControlSectionProps): React.ReactElement {
  const [config, setConfig] = useState<AppConfigDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [android, setAndroid] = useState<PlatformVersionConfig>(emptyPlatformConfig(appKey, 'android'));
  const [ios, setIos] = useState<PlatformVersionConfig>(emptyPlatformConfig(appKey, 'ios'));
  const [updateMessage, setUpdateMessage] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [fillingLive, setFillingLive] = useState(false);
  const sendNotificationInFlightRef = useRef(false);

  const applyConfigToForm = (data: AppConfigDto) => {
    setConfig(data);
    setAndroid(
      normalizeSlice(
        data.android || {
          latestVersion: data.latestVersion,
          minRequiredVersion: data.minRequiredVersion,
          latestVersionCode: data.latestVersionCode,
          minRequiredVersionCode: data.minRequiredVersionCode,
          updateType: data.updateType,
          storeUrl: data.playstoreUrl || DEFAULT_PLAYSTORE_URLS[appKey],
          disableUpdateCheck: Boolean(data.disableUpdateCheckAndroid ?? data.disableUpdateCheck),
        },
        appKey,
        'android'
      )
    );
    setIos(
      normalizeSlice(
        data.ios || {
          latestVersion: data.latestVersion,
          minRequiredVersion: data.minRequiredVersion,
          latestVersionCode: data.latestVersionCode,
          minRequiredVersionCode: data.minRequiredVersionCode,
          updateType: data.updateType,
          storeUrl: data.appstoreUrl || DEFAULT_APPSTORE_URLS[appKey],
          disableUpdateCheck: Boolean(data.disableUpdateCheckIos ?? data.disableUpdateCheck),
        },
        appKey,
        'ios'
      )
    );
    setUpdateMessage(data.updateMessage || '');
    setMaintenanceMode(data.maintenanceMode ?? false);
  };

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAppConfig(appKey);
      applyConfigToForm(data);
    } catch (err: unknown) {
      console.error(`Error fetching app config (${appKey}):`, err);
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(message || `Failed to load config for ${APP_VERSION_LABELS[appKey]}.`);
    } finally {
      setLoading(false);
    }
  }, [appKey]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    if (!android.latestVersion.trim() || !android.minRequiredVersion.trim()) {
      setError('Android latest version and min required version are required.');
      return;
    }
    if (!ios.latestVersion.trim() || !ios.minRequiredVersion.trim()) {
      setError('iOS latest version and min required version are required.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const saved = await updateAppConfig(appKey, {
        android: {
          ...android,
          latestVersion: android.latestVersion.trim(),
          minRequiredVersion: android.minRequiredVersion.trim(),
          storeUrl: android.storeUrl.trim(),
        },
        ios: {
          ...ios,
          latestVersion: ios.latestVersion.trim(),
          minRequiredVersion: ios.minRequiredVersion.trim(),
          storeUrl: ios.storeUrl.trim(),
        },
        updateMessage: updateMessage.trim() || null,
        maintenanceMode,
      });
      applyConfigToForm(saved);
      setSuccess(`${APP_VERSION_LABELS[appKey]} Android and iOS config saved.`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(message || 'Failed to save app config.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendUpdateNotification = async () => {
    if (sendNotificationInFlightRef.current) return;
    sendNotificationInFlightRef.current = true;
    try {
      setSendingNotification(true);
      setError(null);
      setSuccess(null);
      const res = await sendUpdateNotification(appKey);
      const skipped = res.skippedUpToDate ?? 0;
      const androidSent = res.android?.successCount ?? 0;
      const iosSent = res.ios?.successCount ?? 0;
      setSuccess(
        `${res.message} Android sent: ${androidSent}. iOS sent: ${iosSent}.` +
          (res.totalTarget > 0 ? ` Failed: ${res.failureCount}.` : '') +
          (skipped > 0 ? ` Skipped (already updated): ${skipped}.` : '')
      );
      setTimeout(() => setSuccess(null), 6000);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string; message?: string } } }).response?.data
              ?.error ||
            (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to send update notification.');
    } finally {
      sendNotificationInFlightRef.current = false;
      setSendingNotification(false);
    }
  };

  const handleFillLiveVersions = async () => {
    try {
      setFillingLive(true);
      setError(null);
      setSuccess(null);
      const res = await getLiveStoreVersions(appKey);
      const live = res.data;
      setAndroid((prev) => applyLiveSlice(prev, live.android));
      setIos((prev) => applyLiveSlice(prev, live.ios));

      const parts: string[] = [];
      if (live.android?.latestVersion) {
        parts.push(`Android ${live.android.latestVersion}`);
      } else {
        parts.push('Android version not found on Play');
      }
      if (live.android?.latestVersionCode) {
        parts.push(`build ${live.android.latestVersionCode} from devices`);
      }
      if (live.ios?.latestVersion) {
        parts.push(`iOS ${live.ios.latestVersion}`);
      } else {
        parts.push('iOS version not found on App Store');
      }
      if (live.ios?.latestVersionCode) {
        parts.push(`build ${live.ios.latestVersionCode} from devices`);
      }
      setSuccess(
        `Filled latest fields: ${parts.join('; ')}. Min required is unchanged — copy latest into min if you want a force lock. Click Save to apply.`
      );
      setTimeout(() => setSuccess(null), 10000);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string; message?: string } } }).response?.data
              ?.error ||
            (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to fetch live store versions.');
    } finally {
      setFillingLive(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[180px]">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const wrapperClass = embedded
    ? 'overflow-hidden'
    : 'bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden';

  return (
    <div className={wrapperClass}>
      <div
        className={`px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
          embedded ? '' : 'border-b border-gray-200 dark:border-gray-700'
        }`}
      >
        <div>
          {!embedded && (
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {APP_VERSION_LABELS[appKey]}
            </h2>
          )}
          <p className={`text-xs text-gray-500 dark:text-gray-400 ${embedded ? '' : 'mt-0.5'}`}>
            {appKey === 'plus'
              ? 'Push targets vendor devices. Android and iOS mins are independent.'
              : 'Push targets user devices. Android and iOS mins are independent.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleFillLiveVersions}
            disabled={fillingLive || saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm"
          >
            {fillingLive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Fill latest from stores
          </button>
          <button
            type="button"
            onClick={handleSendUpdateNotification}
            disabled={sendingNotification}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {sendingNotification ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Send notification
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 text-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save both platforms
          </button>
        </div>
      </div>

      {(error || success) && (
        <div className="px-4 sm:px-6 pt-4 space-y-2">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="flex gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 text-sm">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}
        </div>
      )}

      <div className="space-y-6 p-4 sm:p-6">
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-800 dark:text-blue-200">
          Do not copy Play Store versionCode onto iOS. Set iOS min to the build that is actually live on
          the App Store, or iPhone users will stay on the update screen after they update.
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <PlatformCard platform="android" value={android} onChange={setAndroid} appKey={appKey} />
          <PlatformCard platform="ios" value={ios} onChange={setIos} appKey={appKey} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Update message
          </label>
          <textarea
            value={updateMessage}
            onChange={(e) => setUpdateMessage(e.target.value)}
            placeholder="Shown on the in-app update screen for both platforms"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id={`maintenanceMode-${appKey}`}
            checked={maintenanceMode}
            onChange={(e) => setMaintenanceMode(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label
            htmlFor={`maintenanceMode-${appKey}`}
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Maintenance mode (show maintenance screen to all users of this app)
          </label>
        </div>
      </div>
      {config?.updatedAt && (
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400">
          Last updated: {new Date(config.updatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

export default AppVersionControlSection;
